/// <amd-module name="Widgets/SprintBurndownWidget/SprintBurndownSettingsManager" />
import { WidgetTeamContext } from "Dashboards/WidgetContracts/WidgetProps";
import * as TfsWidgetContext from "Tfs/Widget/Context";
import { IVssPageContext } from "VSS/Platform/Context";
import { SettingsManagerBase, WidgetSettingsManagerProps } from "WidgetCommon/ModernWidgetTypes/SettingsManagerBase";
import { SprintBurndownSettings } from "./SprintBurndownSettings";

export class SprintBurndownSettingsManager extends SettingsManagerBase<SprintBurndownSettings> {
    public static settingsAreValid(settings: SprintBurndownSettings): boolean {
        const settingsAreValid = settings != null && settings.team != null && settings.team.projectId != null && settings.team.teamId != null;
        return settingsAreValid;
    }
    public constructor(private pageContext: IVssPageContext, private teamContext: WidgetTeamContext) {
        super();
    }

    public getFeatureName() {
        return "SprintBurndown";
    }

    public isConfigured(widgetProps: WidgetSettingsManagerProps): boolean {
        /*
         * If there's any non-trivial information in settings, we don't have a default configuration.
         * We consider null the same as undefined: Unconfigured.
         *
         * Note: a number of non-configurable widgets in production had empty json settings rather than null. We treat such case as unconfigured so default settings generation can proceed as normal.
         */
        return widgetProps.settings != null && JSON.stringify(JSON.parse(widgetProps.settings)) !== "{}";
    }

    /** This is a Burndown proprietary pattern, which duplicates the common widget settings path. */
    public async hasSatisfactoryConfiguration(widgetProps: WidgetSettingsManagerProps): Promise<boolean> {
        return this.isCorrectlyConfigured(widgetProps);
    }

    public isCorrectlyConfigured(props: WidgetSettingsManagerProps): boolean {
        /*
         * If there's *anything* in data, we don't have a default configuration.
         * We consider null the same as undefined: Unconfigured.
         */
        if (!super.isCorrectlyConfigured(props)) {
            return false;
        }

        const settings = JSON.parse(props.settings!) as SprintBurndownSettings;
        const settingsAreValid = settings != null && settings.team != null && settings.team.projectId != null && settings.team.teamId != null;
        return settingsAreValid;
    }

    /**
     * Provides promise to perform neccessary legwork to produce default Burndown Settings, if possible.
     */
    public async generateDefaultSettings(): Promise<SprintBurndownSettings> {
        return Promise.resolve({
            isLegacy: true,
            team: {
                projectId: TfsWidgetContext.getProject(this.pageContext).id,
                teamId: this.teamContext.id
            }
        } as SprintBurndownSettings);
    }
}

export function isLegacyContribution(props: WidgetSettingsManagerProps): boolean {
    return props.contributionId === "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.SprintBurndownWidget";
}
export function isLegacyFlagSuppressed(props: WidgetSettingsManagerProps): boolean {
    if (props != null && props.settings != null) {
        const settings = JSON.parse(props.settings);
        if (settings != null && settings.isLegacy === false) {
            return true;
        }
    }
    return false;
}

export function isLegacyWidget(props: WidgetSettingsManagerProps): boolean {
    return isLegacyContribution(props) && !isLegacyFlagSuppressed(props);
}

export function filterSettings(settings: SprintBurndownSettings): SprintBurndownSettings {
    return {
        isLegacy: true,
        team: settings.team
    };
}
